import { z } from 'zod';

import { WebsitePublishStatusSchema } from './admin-website-pages.js';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Website content review queue - blogs / destination pages / category pages
 * a WEBSITE_SEO editor has sent to a website manager for a decision.
 *
 * Review is opt-in (SEO keeps publish rights), so this queue only fills when
 * an editor chooses "submit for review". Deliberately mirrors the catalog
 * approval contract so the dashboard can render both in one tabbed card.
 */

export const WebsiteApprovalEntityTypeSchema = z.enum([
  'BLOG',
  'DESTINATION_PAGE',
  'CATEGORY_PAGE',
]);
export type WebsiteApprovalEntityType = z.infer<typeof WebsiteApprovalEntityTypeSchema>;

export const WebsiteApprovalActionSchema = z.enum(['SUBMITTED', 'APPROVED', 'REJECTED']);
export type WebsiteApprovalAction = z.infer<typeof WebsiteApprovalActionSchema>;

export const WebsiteApprovalItemSchema = z.object({
  entityType: WebsiteApprovalEntityTypeSchema,
  id: UuidSchema,
  title: z.string(),
  slug: z.string(),
  status: WebsitePublishStatusSchema,
  submittedById: UuidSchema.nullable(),
  /** Resolved at read time; null when the submitting user was deleted. */
  submittedByName: z.string().nullable(),
  submittedAt: IsoDateTimeSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
});
export type WebsiteApprovalItem = z.infer<typeof WebsiteApprovalItemSchema>;

export const WebsiteApprovalListSchema = z.object({
  data: z.array(WebsiteApprovalItemSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});
export type WebsiteApprovalList = z.infer<typeof WebsiteApprovalListSchema>;

export const WebsiteApprovalsQuerySchema = z.object({
  type: z.union([WebsiteApprovalEntityTypeSchema, z.literal('ALL')]).optional(),
  search: z.string().trim().min(1).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export type WebsiteApprovalsQuery = z.infer<typeof WebsiteApprovalsQuerySchema>;

export const WebsiteApprovalLogEntrySchema = z.object({
  id: UuidSchema,
  action: WebsiteApprovalActionSchema,
  actorName: z.string().nullable(),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type WebsiteApprovalLogEntry = z.infer<typeof WebsiteApprovalLogEntrySchema>;

export const WebsiteApprovalDecisionSchema = z.object({
  note: z.string().trim().max(1000).optional(),
});
export type WebsiteApprovalDecision = z.infer<typeof WebsiteApprovalDecisionSchema>;

/** One queue row is not addressable by id alone - the queue mixes three tables. */
export const WebsiteApprovalRefSchema = z.object({
  type: WebsiteApprovalEntityTypeSchema,
  id: UuidSchema,
});
export type WebsiteApprovalRef = z.infer<typeof WebsiteApprovalRefSchema>;

export const WebsiteBulkDecisionRequestSchema = z.object({
  items: z.array(WebsiteApprovalRefSchema).min(1).max(100),
  note: z.string().trim().max(1000).optional(),
});
export type WebsiteBulkDecisionRequest = z.infer<typeof WebsiteBulkDecisionRequestSchema>;

/** Partial success: a bad id lands in `failed`, the rest still go through. */
export const WebsiteBulkDecisionResultSchema = z.object({
  updated: z.number().int().nonnegative(),
  failed: z.array(z.object({ id: UuidSchema, error: z.string() })),
});
export type WebsiteBulkDecisionResult = z.infer<typeof WebsiteBulkDecisionResultSchema>;
